import { useQuery } from "@tanstack/react-query";
import {
  fetchFuturesHistory,
  fetchPowerSpotSeries,
  fetchPowerGeneration,
  type FuturesHistoryPayload,
  type PowerSpotSeriesPayload,
  type PowerGenerationPayload,
} from "../lib/api";
import { FuturesCurveChart } from "./FuturesCurveChart";
import { PowerSpotChart } from "./PowerSpotChart";
import { PowerGenerationChart } from "./PowerGenerationChart";

export function PowerOverviewTab() {
  // Overview tabs don't poll — colleagues use this in customer-facing
  // meetings where mid-conversation chart redraws are disruptive, so
  // `refetchInterval: false` is deliberate. But `staleTime: Infinity` was
  // the opposite problem: after a month/quarter rollover the server-side
  // cache (chart-api `futuresCache`, 1 h TTL) already includes the newly
  // tradable Feb-2027/Q3-2028/etc., but a long-lived browser tab never
  // refetched that payload and the new contracts never showed. Trade: a
  // 1 h client staleTime (matches the server) plus `refetchOnWindowFocus:
  // true` so the "I came back to the dashboard the next day" case picks
  // up the new rollover payload silently — single revalidation per focus
  // event, not continuous polling.
  const futures = useQuery<FuturesHistoryPayload, Error>({
    queryKey: ["futures-history"],
    queryFn: () => fetchFuturesHistory(false),
    refetchInterval: false,
    refetchOnWindowFocus: true,
    staleTime: 60 * 60 * 1000,
  });
  const spot = useQuery<PowerSpotSeriesPayload, Error>({
    queryKey: ["power-spot-series"],
    queryFn: () => fetchPowerSpotSeries(false),
    refetchInterval: false,
    refetchOnWindowFocus: true,
    staleTime: 60 * 60 * 1000,
  });
  const generation = useQuery<PowerGenerationPayload, Error>({
    queryKey: ["power-generation"],
    queryFn: () => fetchPowerGeneration(false),
    refetchInterval: false,
    refetchOnWindowFocus: true,
    staleTime: 60 * 60 * 1000,
  });

  const baseCurve = futures.data?.curves.find((c) => c.id === "power-base");
  const peakCurve = futures.data?.curves.find((c) => c.id === "power-peak");

  return (
    <div className="space-y-4 sm:space-y-5">
      {futures.isError && (
        <ErrorBanner
          title="Terminmarkt-Historie konnte nicht geladen werden"
          message={futures.error.message}
          onRetry={() => futures.refetch()}
        />
      )}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
        {futures.isLoading ? (
          <>
            <ChartSkeleton />
            <ChartSkeleton />
          </>
        ) : (
          <>
            {baseCurve && <FuturesCurveChart curve={baseCurve} updatedAt={futures.data!.updatedAt} />}
            {peakCurve && <FuturesCurveChart curve={peakCurve} updatedAt={futures.data!.updatedAt} />}
          </>
        )}
      </div>

      {spot.isError && (
        <ErrorBanner
          title="Spot/Prognose konnte nicht geladen werden"
          message={spot.error.message}
          onRetry={() => spot.refetch()}
        />
      )}
      {spot.isLoading ? <ChartSkeleton tall /> : spot.data && <PowerSpotChart data={spot.data} />}

      {generation.isError && (
        <ErrorBanner
          title="Wind/Solar Erzeugung konnte nicht geladen werden"
          message={generation.error.message}
          onRetry={() => generation.refetch()}
        />
      )}
      {generation.isLoading ? (
        <ChartSkeleton tall />
      ) : (
        generation.data && <PowerGenerationChart data={generation.data} />
      )}
    </div>
  );
}

function ChartSkeleton({ tall = false }: { tall?: boolean }) {
  return (
    <div
      className={`rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 animate-pulse ${
        tall ? "h-80" : "h-72"
      }`}
    />
  );
}

function ErrorBanner({
  title,
  message,
  onRetry,
}: {
  title: string;
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="rounded-lg border border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 px-4 py-3 text-sm text-rose-900 dark:text-rose-200 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="font-medium truncate">{title}</p>
        <p className="text-xs opacity-80 truncate">{message}</p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="shrink-0 rounded-md bg-rose-900 text-white px-2.5 py-1 text-xs font-medium hover:bg-rose-800"
      >
        Erneut
      </button>
    </div>
  );
}
